import { useMemo, useState } from "react";
import { useViewport } from "src/contexts";
import { SummaryDrawer } from "src/pages/configurator/components/SummaryDrawer";
import { useConfiguratorContext } from "src/pages/configurator/Configurator.context";
import { getStepDisabledReasons } from "src/pages/configurator/Configurator.utils";
import { Button, ButtonProps, Icon, IconButton, TooltipV2 } from "~components";
import { Css } from "~generated/css";
import { CheckoutConfigStatus } from "~generated/graphqlTypes";
import { currencyFormatter, getProgramDataStrings, useTestIds } from "~utils";
import { FlowStep, FlowSteps } from "../../stepTemplates";

type ConfiguratorStepperProps = {
  actionBts?: ButtonProps[];
  onSummaryDrawerToggle?: (isOpen: boolean) => void;
};

export const FooterHeight = 64;

const footerStyles = Css.bgGray800.px4.py2.hPx(FooterHeight).w100.df.jcsb.asfe.aic.fixed.bottom0.z2.fs0.ifTablet.px3.$;

export function ConfiguratorStepper({ actionBts, onSummaryDrawerToggle }: ConfiguratorStepperProps) {
  const contextState = useConfiguratorContext();
  const { goToPreviousStep, goToNextStep, currentStep, checkoutConfig } = contextState;
  const tid = useTestIds({}, "configurator-stepper");
  const { isMobile } = useViewport();
  const [showDrawer, setShowDrawer] = useState(false);

  const disabledReasons = useMemo(() => {
    return checkoutConfig && getStepDisabledReasons(contextState).map(({ disabledReason }) => disabledReason);
  }, [contextState, checkoutConfig]);

  function handleDrawerToggle(isOpen: boolean) {
    onSummaryDrawerToggle?.(isOpen);
    setShowDrawer(isOpen);
  }

  if (checkoutConfig?.status === CheckoutConfigStatus.Expired) {
    return null;
  }

  const totalData = getProgramDataStrings(checkoutConfig?.programData);
  const aduData = getProgramDataStrings(checkoutConfig?.aduProgramData);
  const pof = checkoutConfig?.productOffering;
  const aduPof = checkoutConfig?.aduProductOffering;

  const isSummaryStep = currentStep === FlowSteps[FlowStep.Summary];

  const Actions = () =>
    actionBts ? (
      actionBts.map((props, i) => (
        <div key={`stepper-button-${i}`}>
          <Button {...props} {...tid.action_button} />
        </div>
      ))
    ) : (
      <>
        {checkoutConfig?.status === CheckoutConfigStatus.Draft && (
          <>
            {currentStep.sortOrder !== 1 && (
              <div>
                <Button
                  variant="link"
                  color="white"
                  onClick={() => {
                    handleDrawerToggle(false);
                    goToPreviousStep();
                  }}
                  {...tid.back_button}
                >
                  Back
                </Button>
              </div>
            )}
            {/* If the button is disabled tooltip v2 won't work, it would be awesome if this could be pulled into the Button component */}
            <span id="stepper-next-btn">
              <Button
                variant="secondary"
                disabled={disabledReasons && disabledReasons?.length > 0}
                tooltipPlacement="top"
                onClick={() => {
                  handleDrawerToggle(false);
                  goToNextStep();
                }}
                loadingText="Saving"
                {...tid.next_button}
              >
                Next
              </Button>
            </span>
            {disabledReasons && disabledReasons.length > 0 && (
              <TooltipV2 anchorSelect="#stepper-next-btn">
                <ul css={Css.add("paddingInlineStart", "24px").add("marginBlock", "0px").$}>
                  {disabledReasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              </TooltipV2>
            )}
          </>
        )}
      </>
    );

  return (
    <>
      {!isSummaryStep && <SummaryDrawer onClose={() => handleDrawerToggle(false)} isOpen={showDrawer} />}
      {/* TODO: Update mobile footer to show ADU plan info when aduProductOffering exists */}
      {pof && isMobile && !isSummaryStep && !showDrawer && (
        <div
          id="mobile-receipt-summary"
          css={{
            ...footerStyles,
            ...Css.bgWhite.ba.bcLightGray.bottom8.$,
          }}
        >
          <div css={Css.t14.gray.$}>Estimated Price</div>
          <div css={Css.df.gap1.t18.$}>
            <div css={Css.df.gap1.t14.aic.$}>
              <div>{currencyFormatter(checkoutConfig?.totalPriceInCents)}</div>
              <div>
                <IconButton
                  hideBorder
                  size="small"
                  onClick={() => handleDrawerToggle(!showDrawer)}
                  icon="arrow-up"
                  {...tid.mobile_summary_button}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <div css={footerStyles}>
        {pof && !isMobile && (
          <div css={Css.df.$}>
            <>
              <div css={Css.df.aic.gapPx(40).f1.pr5.bcGray.br.$}>
                <ProgramDataBlock name={pof.name} data={totalData} />
                {aduPof && (
                  <>
                    <Icon name="plus-gray" />
                    <ProgramDataBlock name={aduPof.name} data={aduData} />
                  </>
                )}
              </div>

              <div css={Css.df.fdc.gap1.px5.$}>
                <div css={Css.t14.white.$}>{currencyFormatter(checkoutConfig?.totalPriceInCents)}</div>
                <div
                  onClick={() => !isSummaryStep && handleDrawerToggle(!showDrawer)}
                  css={Css.df.gap("20px").t12.white.if(!isSummaryStep).tdu.cursorPointer.$}
                >
                  Estimated Price
                </div>
              </div>
              {!isSummaryStep && (
                <div css={Css.asc.if(showDrawer).add("transform", "rotate(180deg)").$}>
                  <IconButton
                    size="small"
                    onClick={() => handleDrawerToggle(!showDrawer)}
                    icon="arrow-up"
                    {...tid.summary_button}
                  />
                </div>
              )}
            </>
          </div>
        )}
        {/* Mobile footer will display actions on each side of the stepper. Tablet & up will display actions on the right side. */}
        {isMobile ? (
          <Actions />
        ) : (
          <div css={Css.df.gap2.$}>
            <Actions />
          </div>
        )}
      </div>
    </>
  );
}

export function CommunityOrLotStepper(props: { onNext: () => void; onBack?: () => void; disabledReasons?: string[] }) {
  const { onNext, onBack, disabledReasons } = props;
  const tid = useTestIds({}, "configurator-stepper");
  return (
    <div css={{ ...footerStyles, ...Css.ifTabletAndUp.jcfe.gap2.$ }}>
      <div /* div keeps Button from being 100% wide */>
        {onBack && (
          <Button variant="link" color="white" onClick={onBack} {...tid.backButton}>
            Back
          </Button>
        )}
      </div>
      <div>
        <span id="stepper-next-btn">
          <Button
            variant="secondary"
            disabled={disabledReasons && disabledReasons.length > 0}
            tooltipPlacement="top"
            onClick={() => {
              if (!disabledReasons || disabledReasons.length === 0) onNext();
            }}
            {...tid.next_button}
          >
            Next
          </Button>
        </span>
        {disabledReasons && disabledReasons.length > 0 && (
          <TooltipV2 anchorSelect="#stepper-next-btn">
            <ul css={Css.add("paddingInlineStart", "24px").add("marginBlock", "0px").$}>
              {disabledReasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          </TooltipV2>
        )}
      </div>
    </div>
  );
}

function ProgramDataBlock({ name, data }: { name: string; data: ReturnType<typeof getProgramDataStrings> }) {
  return (
    <div css={Css.df.fdc.gap1.$}>
      <div css={Css.t14.white.$}>{name}</div>
      <div css={Css.df.gapPx(20).t12.white.$}>
        <div>{data.bedrooms}</div>
        <div>{data.bathrooms}</div>
        <div>{data.sqft}</div>
      </div>
    </div>
  );
}

// Dummy stepper for loading states.
export function SkeletonStepper({ showBack = false }: { showBack?: boolean }) {
  const tid = useTestIds({}, "skeleton-stepper");
  return (
    <div css={{ ...footerStyles, ...Css.ifTabletAndUp.jcfe.gap2.$ }}>
      <div /* div keeps Button from being 100% wide */>
        {showBack && (
          <Button variant="link" color="white" disabled {...tid.backButton}>
            Back
          </Button>
        )}
      </div>
      <div>
        <span id="stepper-next-btn">
          <Button variant="secondary" tooltipPlacement="top" disabled {...tid.next_button}>
            Next
          </Button>
        </span>
      </div>
    </div>
  );
}
